import { CONSTANTS } from 'config/constants';
import { Action } from 'models/action';
import { ActionSet } from 'models/action-set';
import {
    Order,
    Restoration,
    RestorationAction,
    RestorationJaw,
    RestorationMaterial,
    RestorationPrice,
    RestorationProperty,
    RestorationPropertyValueType,
    RestorationTeeth,
    RestorationType,
    TeethSelectionType,
    ToothState,
} from 'models/order';
import { ActionConstruction, ActionConstructionPropertyValues } from 'models/order-input/action-order';
import { TeethRange } from 'models/material';
import { LabConstuction } from 'models/order-input/lab-order';
import {
    ActionUnitInput,
    RestorationActionInput,
    RestorationInput,
    RestorationMaterialInput,
    RestorationPropertyInput,
    RestorationPropertyValue,
    RestorationTeethInput,
} from 'models/order-input/restoration-input';
import { ActionGridInput, MaterialGridInput } from 'models/order-input/shared';
import { SimpleSubCategory } from 'models/order-input/simple-order';
import { AvailableConstructionProperties, ConstructionProperty } from 'models/property';

/**
 * DD-2506 step 3 — the flows' editing state ↔ the one `RestorationInput` the server takes,
 * and the stored restorations ↔ that editing state when an order is modified. The simple
 * and supplier flows edit `RestorationInput` itself; every other flow keeps its own form
 * model, and this is the single place that knows how each one maps.
 */

const UPPER_JAW = CONSTANTS.UPPER_JAW.map(Number);
const LOWER_JAW = CONSTANTS.LOWER_JAW.map(Number);

// ---------------------------------------------------------------------------------------
// Properties: the flows' camelCase field names ↔ the stored SCREAMING_SNAKE keys.
// ---------------------------------------------------------------------------------------

/** Field name in the flows' form models → stored restoration property key. */
export const PROPERTY_KEY_BY_FIELD: Record<string, string> = {
    material: 'MATERIAL',
    otherMaterial: 'OTHER_MATERIAL',
    shade: 'SHADE',
    color: 'COLOR',
    colorSampling: 'COLOR_SAMPLING',
    claspMaterial: 'CLASP_MATERIAL',
    orthColor: 'ORTHODONTICS_COLORS',
    facialLoop: 'FACIAL_LOOP',
    claspType: 'CLASP_TYPES',
    springType: 'SPRING_TYPES',
    design: 'METAL_DESIGN',
    testingSteps: 'TESTING_STEPS',
    implantSteps: 'IMPLANT_STEPS',
    dentureSteps: 'DENTURE_STEPS',
    ponticDesign: 'PONTIC_DESIGN',
    pontic: 'PONTIC_DESIGN',
    contactType: 'CONTACT_TYPE',
    retainment: 'RETAINMENT',
    occlusion: 'OCCLUSION_TYPE',
    claspTeeth: 'CLASP_TEETH',
    springTeeth: 'SPRING_TEETH',
    expansionScrew: 'EXPANSION_SCREW',
    protrusion: 'PROTRUSION',
    softInside: 'SOFT_INSIDE',
    warrantyType: 'WARRANTY_TYPE',
};

/** Stored key → the field name the forms use (the first spelling wins: `ponticDesign`, not `pontic`). */
const FIELD_BY_PROPERTY_KEY: Record<string, string> = Object.entries(PROPERTY_KEY_BY_FIELD).reduce(
    (fields, [field, key]) => (fields[key] ? fields : { ...fields, [key]: field }),
    {} as Record<string, string>,
);

const BOOLEAN_KEYS = new Set(['COLOR_SAMPLING', 'EXPANSION_SCREW', 'SOFT_INSIDE']);
const TEETH_LIST_KEYS = new Set(['CLASP_TEETH', 'SPRING_TEETH']);

const isYes = (value: unknown) => value === true || value === 'YES' || value === 'true';

const toothNumbers = (value: unknown): number[] =>
    Array.isArray(value)
        ? value
              .map((tooth) => (typeof tooth === 'object' && tooth ? (tooth as { id: number }).id : tooth))
              .map(Number)
              .filter((tooth) => !isNaN(tooth))
        : [];

/**
 * The property inputs for a flow's property bag. Empty values are left out; a boolean is
 * sent only when set, a tooth list only when it has teeth. `warrantyType` goes along only
 * on a warranty restoration — the forms save their default ("NONE") on every construction.
 */
export const propertiesToInputs = (
    properties: Record<string, unknown> | undefined,
    options: { warranty?: boolean } = {},
): RestorationPropertyInput[] => {
    const inputs: RestorationPropertyInput[] = [];
    Object.entries(properties ?? {}).forEach(([field, raw]) => {
        const key = PROPERTY_KEY_BY_FIELD[field];
        if (!key || raw === undefined || raw === null || raw === '') return;
        if (key === 'WARRANTY_TYPE') {
            if (options.warranty && raw !== 'NONE') inputs.push({ key, value: String(raw) });
            return;
        }
        if (BOOLEAN_KEYS.has(key)) {
            if (isYes(raw)) inputs.push({ key, value: true });
            return;
        }
        if (TEETH_LIST_KEYS.has(key)) {
            const teeth = toothNumbers(raw);
            if (teeth.length) inputs.push({ key, value: teeth });
            return;
        }
        if (typeof raw === 'string' || typeof raw === 'number') {
            inputs.push({ key, value: raw });
        }
    });
    return inputs;
};

/** The stored properties back into a flow's property bag (camelCase, form-shaped values). */
export const propertiesFromRestoration = (
    restoration: Restoration,
    options: { teethAsIds?: boolean } = {},
): Record<string, RestorationPropertyValue | { id: number }[]> => {
    const bag: Record<string, RestorationPropertyValue | { id: number }[]> = {};
    (restoration.properties ?? []).forEach((property) => {
        const field = FIELD_BY_PROPERTY_KEY[property.key];
        if (!field) return;
        switch (property.valueType) {
            case RestorationPropertyValueType.BOOLEAN_VALUE:
                bag[field] = Boolean(property.flag ?? property.value);
                break;
            case RestorationPropertyValueType.LIST_OF_TEETH: {
                const teeth = property.teeth ?? toothNumbers(property.value);
                bag[field] = options.teethAsIds ? teeth.map((id) => ({ id })) : teeth;
                break;
            }
            case RestorationPropertyValueType.VALUE_AND_LABEL:
                break;
            default:
                bag[field] = property.text ?? (property.value as string | number);
        }
    });
    return bag;
};

/** The unit counts of a restoration, keyed by the catalogue action of the row they belong to. */
export const unitCountsFromRestoration = (
    restoration: Restoration,
): { header: string; numberOfUnits: number; actionId: string }[] => {
    const units = restoration.properties?.find((property) => property.key === 'NUMBER_OF_UNITS')?.units ?? [];
    return units.map((unit) => {
        const row = restoration.actions.find((action) => action._id === unit.actionRowId);
        return { header: unit.label, numberOfUnits: unit.value, actionId: row?.actionId ?? unit.actionRowId };
    });
};

// ---------------------------------------------------------------------------------------
// Teeth
// ---------------------------------------------------------------------------------------

const sameSet = (a: number[], b: number[]) => a.length === b.length && a.every((tooth) => b.includes(tooth));

const TOOTH_STATE_BY_STRUCTURE_VALUE: Record<number, ToothState> = {
    2: ToothState.PONTIC,
    3: ToothState.EXTRACTED,
    4: ToothState.MISSING,
};
const STRUCTURE_VALUE_BY_TOOTH_STATE: Record<ToothState, number> = {
    [ToothState.PONTIC]: 2,
    [ToothState.EXTRACTED]: 3,
    [ToothState.MISSING]: 4,
};

/**
 * Teeth picked one by one (the lab flow's tooth chart, the action flow's tooth list). A
 * full jaw is sent as a jaw; tooth states other than "selected" travel along.
 */
export const teethInputFromNumbers = (
    teethNumbers: number[] | undefined,
    teethState?: Map<number, number>,
): RestorationTeethInput => {
    const teeth = Array.from(new Set((teethNumbers ?? []).map(Number).filter((tooth) => !isNaN(tooth))));
    if (!teeth.length) return { type: TeethSelectionType.NONE };
    const toothStates = teeth
        .map((tooth) => ({ tooth, state: TOOTH_STATE_BY_STRUCTURE_VALUE[teethState?.get(tooth) ?? 1] }))
        .filter((entry): entry is { tooth: number; state: ToothState } => Boolean(entry.state));
    const upper = sameSet(teeth, UPPER_JAW);
    const lower = sameSet(teeth, LOWER_JAW);
    const both = sameSet(teeth, [...UPPER_JAW, ...LOWER_JAW]);
    if (upper || lower || both) {
        return {
            type: TeethSelectionType.JAW,
            selectedJaw: both ? RestorationJaw.BOTH_JAWS : upper ? RestorationJaw.UPPER_JAW : RestorationJaw.LOWER_JAW,
            selectedTeeth: teeth,
            ...(toothStates.length ? { toothStates } : {}),
        };
    }
    return { type: TeethSelectionType.TEETH, selectedTeeth: teeth, ...(toothStates.length ? { toothStates } : {}) };
};

export const teethInputFromRange = (range: { start: number; end: number } | undefined): RestorationTeethInput =>
    range
        ? { type: TeethSelectionType.RANGE, selectedRange: { start: range.start, end: range.end } }
        : { type: TeethSelectionType.NONE };

const jawTeeth = (jaw: RestorationJaw | null | undefined): number[] => {
    switch (jaw) {
        case RestorationJaw.UPPER_JAW:
            return UPPER_JAW;
        case RestorationJaw.LOWER_JAW:
            return LOWER_JAW;
        case RestorationJaw.BOTH_JAWS:
            return [...UPPER_JAW, ...LOWER_JAW];
        default:
            return [];
    }
};

/** The stored teeth as the tooth chart edits them: the numbers plus a state per tooth. */
export const teethNumbersFromRestoration = (
    teeth: RestorationTeeth | undefined,
): { teethNumbers: number[]; teethState: Map<number, number> } => {
    let teethNumbers: number[] = [];
    switch (teeth?.type) {
        case TeethSelectionType.TEETH:
            teethNumbers = teeth.selectedTeeth ?? [];
            break;
        case TeethSelectionType.JAW:
            teethNumbers = teeth.selectedTeeth?.length ? teeth.selectedTeeth : jawTeeth(teeth.selectedJaw);
            break;
        case TeethSelectionType.RANGE:
            teethNumbers = teeth.selectedRange ? expandRange(teeth.selectedRange.start, teeth.selectedRange.end) : [];
            break;
        default:
            teethNumbers = [];
    }
    const teethState = new Map<number, number>(teethNumbers.map((tooth) => [tooth, 1]));
    (teeth?.toothStates ?? []).forEach((entry) => {
        teethState.set(entry.tooth, STRUCTURE_VALUE_BY_TOOTH_STATE[entry.state] ?? 1);
    });
    return { teethNumbers, teethState };
};

/** The FDI teeth between two numbers along the arch (the jaw arrays run 18 → 28, 48 → 38). */
export const expandRange = (start: number, end: number): number[] => {
    const arch = [...UPPER_JAW, ...LOWER_JAW];
    const from = arch.indexOf(start);
    const to = arch.indexOf(end);
    if (from === -1 || to === -1) return [start, end].filter((tooth) => !isNaN(tooth));
    return arch.slice(Math.min(from, to), Math.max(from, to) + 1);
};

// ---------------------------------------------------------------------------------------
// Jaw selections: the picker holds a jaw as the range between its outermost teeth
// ---------------------------------------------------------------------------------------

/**
 * The tooth picker has no jaw to hand over, only teeth and a range, so it holds one jaw as the
 * range between that jaw's outermost teeth and both jaws as the range from the first upper
 * tooth to the last lower one (DD-3585, DD-3765). Nothing else produces a range across the two
 * jaws: the picker only ever builds a range within one of them.
 */
const JAW_RANGES: [RestorationJaw, TeethRange][] = [
    [RestorationJaw.UPPER_JAW, { start: UPPER_JAW[0], end: UPPER_JAW[UPPER_JAW.length - 1] }],
    [RestorationJaw.LOWER_JAW, { start: LOWER_JAW[0], end: LOWER_JAW[LOWER_JAW.length - 1] }],
    [RestorationJaw.BOTH_JAWS, { start: UPPER_JAW[0], end: LOWER_JAW[LOWER_JAW.length - 1] }],
];

/** The jaw a picker range stands for, either way round, or nothing for an ordinary range. */
export const jawOfRange = (range: TeethRange): RestorationJaw | undefined =>
    JAW_RANGES.find(
        ([, ends]) =>
            (range.start === ends.start && range.end === ends.end) ||
            (range.start === ends.end && range.end === ends.start),
    )?.[0];

/** The range the picker holds a jaw as, for loading a stored jaw back into it. */
export const rangeOfJaw = (jaw: RestorationJaw): TeethRange | undefined =>
    JAW_RANGES.find(([entry]) => entry === jaw)?.[1];

/** The jaws a jaw selection covers: what a per-tooth action priced by jaw is charged for. */
export const jawCountOf = (jaw: RestorationJaw | null | undefined): number =>
    jaw === RestorationJaw.BOTH_JAWS ? 2 : 1;

/** A picked range, or a jaw selection sent as the jaw it is (DD-3585). */
export const jawTeethInputFromRange = (range: TeethRange): RestorationTeethInput => {
    const jaw = jawOfRange(range);
    return jaw ? { type: TeethSelectionType.JAW, selectedJaw: jaw } : teethInputFromRange(range);
};

/**
 * A stored jaw selection from the jaw picker, as the picker holds it. A jaw that lists its
 * teeth was clicked tooth by tooth and loads back as those teeth.
 */
export const jawPickerRangeOfTeeth = (teeth: RestorationTeeth): TeethRange | undefined =>
    teeth.type === TeethSelectionType.JAW && !teeth.selectedTeeth?.length && teeth.selectedJaw
        ? rangeOfJaw(teeth.selectedJaw)
        : undefined;

// ---------------------------------------------------------------------------------------
// Dentist simple order / supplier order: the flows edit RestorationInput itself
// ---------------------------------------------------------------------------------------

export const RESTORATION_TYPE_BY_SIMPLE_CATEGORY: Record<SimpleSubCategory, RestorationType> = {
    [SimpleSubCategory.SINGLE_CROWN]: RestorationType.CROWN,
    [SimpleSubCategory.BRIDGE]: RestorationType.BRIDGE,
    [SimpleSubCategory.POST]: RestorationType.POST_CORE,
    [SimpleSubCategory.IMPLANT_CROWN]: RestorationType.IMPLANT_CROWN,
    [SimpleSubCategory.IMPLANT_BRIDGE]: RestorationType.IMPLANT_BRIDGE,
    [SimpleSubCategory.OTHER]: RestorationType.FIXED_PROSTHETICS_OTHER,
    [SimpleSubCategory.COMPLETE_DENTURE]: RestorationType.FULL_DENTURE,
    [SimpleSubCategory.PARTIAL_DENTURE]: RestorationType.PARTIAL_DENTURE,
    [SimpleSubCategory.TEMPORARY_DENTURE]: RestorationType.TEMPORARY_PARTIAL_DENTURE,
    [SimpleSubCategory.BITETRAY]: RestorationType.SPLINT,
    [SimpleSubCategory.OTHER_REMOVABLE]: RestorationType.REMOVABLE_PROSTHETICS_OTHER,
};

const SIMPLE_CATEGORY_BY_RESTORATION_TYPE: Partial<Record<RestorationType, SimpleSubCategory>> = Object.entries(
    RESTORATION_TYPE_BY_SIMPLE_CATEGORY,
).reduce(
    (categories, [category, type]) => ({ ...categories, [type]: category as SimpleSubCategory }),
    {} as Partial<Record<RestorationType, SimpleSubCategory>>,
);

const REMOVABLE_TYPES = new Set<RestorationType>([
    RestorationType.FULL_DENTURE,
    RestorationType.PARTIAL_DENTURE,
    RestorationType.TEMPORARY_PARTIAL_DENTURE,
    RestorationType.SPLINT,
    RestorationType.REMOVABLE_PROSTHETICS_OTHER,
    RestorationType.DIAGNOSTIC_WAXUP,
    RestorationType.RELINE,
]);

/** The simple flow's category for a stored type; types the flow has no card for fall back to "other". */
export const simpleCategoryForType = (type: RestorationType | undefined): SimpleSubCategory =>
    (type && SIMPLE_CATEGORY_BY_RESTORATION_TYPE[type]) ??
    (type && REMOVABLE_TYPES.has(type) ? SimpleSubCategory.OTHER_REMOVABLE : SimpleSubCategory.OTHER);

/** A restoration's properties as the property widgets edit them (camelCase fields, stored values). */
export const propertiesFromInputs = (
    properties: RestorationPropertyInput[] | undefined,
): Record<string, RestorationPropertyValue> =>
    (properties ?? []).reduce(
        (bag, { key, value }) => {
            const field = FIELD_BY_PROPERTY_KEY[key];
            return field ? { ...bag, [field]: value } : bag;
        },
        {} as Record<string, RestorationPropertyValue>,
    );

/** The warranty type a restoration input carries (only a warranty restoration has one). */
export const warrantyTypeOfInput = (restoration: RestorationInput): string | undefined => {
    const property = restoration.properties.find((entry) => entry.key === 'WARRANTY_TYPE');
    return property ? String(property.value) : undefined;
};

const warrantyTypeOf = (restoration: Restoration): string | undefined => {
    const property = restoration.properties?.find((entry) => entry.key === 'WARRANTY_TYPE');
    return property ? String(property.text ?? property.value) : undefined;
};

/**
 * The restoration's own flags decide; the rows only stand in on restorations derived from
 * legacy orders, which have no flags. A simple-clinic order reaches the lab without rows,
 * so reading the rows alone loses the clinic's choice (DD-3855).
 */
export const isWarrantyRestoration = (restoration: Restoration): boolean =>
    restoration.warranty ??
    (restoration.actions.some((row) => row.warranty) || restoration.materials.some((row) => row.warranty));

export const isTioTandvardRestoration = (restoration: Restoration): boolean =>
    restoration.isTioTandvard ?? restoration.actions.some((row) => row.isTioTandvard);

// ---------------------------------------------------------------------------------------
// Clinic action-based order: ActionConstruction
// ---------------------------------------------------------------------------------------

/** Action rows reference the action-set action; the same id is the row's `ref`. */
export const actionConstructionToRestorationInput = (construction: ActionConstruction): RestorationInput => {
    const settings = construction.settings ?? ({} as ActionConstructionPropertyValues);
    const { numberOfUnits, materialId, ...properties } = settings;
    const units: ActionUnitInput[] = (numberOfUnits ?? [])
        .filter((unit) => unit.actionId && unit.numberOfUnits > 0)
        .map((unit) => ({ actionRef: unit.actionId, label: unit.header, value: unit.numberOfUnits }));
    return {
        ...(construction._id ? { _id: construction._id } : {}),
        teeth: construction.teethRange
            ? jawTeethInputFromRange(construction.teethRange)
            : teethInputFromNumbers(construction.teethNumbers),
        properties: [
            ...propertiesToInputs(
                { ...properties, warrantyType: construction.warrantyType },
                { warranty: construction.warranty },
            ),
            ...(units.length ? [{ key: 'NUMBER_OF_UNITS', value: units }] : []),
        ],
        ...(materialId ? { materialId } : {}),
        warranty: Boolean(construction.warranty),
        isTioTandvard: Boolean(construction.isTioTandvard),
        actions: construction.actions.map((action) => {
            const actionId = String(action._id);
            const rowId = construction.actionRowIds?.[actionId];
            return {
                ...(rowId ? { _id: rowId } : {}),
                ref: actionId,
                actionId,
                category: construction.subCategoryId,
            };
        }),
        // The lab's materials are kept by the server for a restoration sent back by _id.
        materials: [],
    };
};

/** The stored restorations as the action flow edits them, actions resolved from the action set. */
export const restorationsToActionConstructions = (order: Order, actionSet: ActionSet): ActionConstruction[] =>
    (order.restorations ?? [])
        .map((restoration) => {
            const actions = restoration.actions
                .map((row) => actionSet.actions.find((action) => action._id === row.actionId))
                .filter((action): action is Action => Boolean(action));
            const actionRowIds = restoration.actions.reduce(
                (ids, row) => (row.actionId ? { ...ids, [row.actionId]: row._id } : ids),
                {} as Record<string, string>,
            );
            const bag = propertiesFromRestoration(restoration, { teethAsIds: true });
            const settings = {
                ...emptyActionSettings(),
                ...bag,
                numberOfUnits: unitCountsFromRestoration(restoration),
            } as ActionConstructionPropertyValues;
            settings.materialId = materialIdFor(actions, settings.material);
            const { teethNumbers } = teethNumbersFromRestoration(restoration.teeth);
            const jawRange = jawPickerRangeOfTeeth(restoration.teeth);
            return {
                _id: restoration._id,
                actions,
                actionRowIds,
                categoryId: actions[0]?.mainCategory?._id,
                subCategoryId: restoration.actions[0]?.category ?? actions[0]?.subCategory?.[0]?._id,
                teethNumbers:
                    restoration.teeth.type === TeethSelectionType.RANGE || jawRange ? undefined : teethNumbers,
                teethRange:
                    restoration.teeth.type === TeethSelectionType.RANGE
                        ? restoration.teeth.selectedRange ?? undefined
                        : jawRange,
                warranty: isWarrantyRestoration(restoration),
                isTioTandvard: isTioTandvardRestoration(restoration),
                warrantyType: warrantyTypeOf(restoration),
                settings,
                animationId: restoration._id,
            } as ActionConstruction;
        })
        // An action deleted from the set (not merely disabled for the lab) has no category or
        // price data left; the flow cannot edit such a restoration.
        .filter((construction) => {
            if (construction.actions.length === 0) {
                console.warn(
                    'restorationsToActionConstructions: dropping a restoration with no resolvable actions',
                    construction,
                );
                return false;
            }
            return true;
        });

const emptyActionSettings = (): ActionConstructionPropertyValues =>
    ({
        material: '',
        materialId: '',
        otherMaterial: '',
        shade: '',
        color: '',
        colorSampling: false,
        design: '',
        implantSteps: '',
        dentureSteps: '',
        ponticDesign: '',
        contactType: '',
        retainment: '',
        occlusion: '',
        orthColor: '',
        softInside: false,
        claspType: '',
        testingSteps: '',
        claspTeeth: [],
        springTeeth: [],
        springType: '',
        claspMaterial: '',
        facialLoop: '',
        expansionScrew: false,
        protrusion: '',
        numberOfUnits: [],
    }) as ActionConstructionPropertyValues;

const materialIdFor = (actions: Action[], materialName: string | undefined): string => {
    if (!materialName) return '';
    for (const action of actions) {
        const match = (action.materialGroup?.materials ?? []).find((material) => material.material === materialName);
        if (match?._id) return match._id;
    }
    return '';
};

// ---------------------------------------------------------------------------------------
// Lab-created order: LabConstuction and the action / material grids
// ---------------------------------------------------------------------------------------

const toNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === 'number') return isNaN(value) ? fallback : value;
    const parsed = parseFloat(String(value ?? '').replace(',', '.'));
    return isNaN(parsed) ? fallback : parsed;
};

/** The action grid rows that become inputs. */
const submittedActionRows = (rows: ActionGridInput[]) =>
    rows.filter((row) => row.actionId || row.rowId || row.description);

/**
 * A row's handle inside one input: its stored row id, else its position. Never the grid
 * `_id`: on a newly picked row that is the price-list row, which two rows of the same
 * action share (DD-3591). `index` is the row's position among `submittedActionRows`.
 */
const actionRef = (row: ActionGridInput, index: number) => row.rowId || `action-${index}`;

/** The action grid's rows as inputs: rows that exist keep their id and restate what the grid edits. */
export const gridToRestorationActionInputs = (rows: ActionGridInput[]): RestorationActionInput[] =>
    submittedActionRows(rows).map((row, index) => ({
        ...(row.rowId ? { _id: row.rowId } : {}),
        ref: actionRef(row, index),
        ...(row.actionId ? { actionId: row.actionId } : {}),
        // A line without a catalogue action is free text, stored or new: its own name and
        // code travel with it so an edit to them is kept. A catalogue line sends its text
        // too, so a lab's own wording on it is kept (DD-3583); its code stays the catalogue's.
        ...(row.description ? { name: row.description } : {}),
        ...(!row.actionId ? { code: row.actionCode || undefined } : {}),
        quantity: toNumber(row.quantity, 1),
        unitPrice: toNumber(row.unitPrice),
        vatPercent: toNumber(row.vatPercent),
    }));

/**
 * The material grid's rows as inputs. A row picked from the price list carries the
 * catalogue material; a row that exists is sent by id; a material added for an action
 * points at that action's ref. A material row knows only its action, not the row, so
 * with the same action on two rows it goes with the first.
 */
export const gridToRestorationMaterialInputs = (
    rows: MaterialGridInput[],
    actionRows: ActionGridInput[],
): RestorationMaterialInput[] => {
    const refByActionId = new Map<string, string>();
    submittedActionRows(actionRows).forEach((row, index) => {
        if (row.actionId && !refByActionId.has(row.actionId)) refByActionId.set(row.actionId, actionRef(row, index));
    });
    return rows
        .filter((row) => row._id || row.rowId || row.description)
        .map((row) => ({
            ...(row.rowId ? { _id: row.rowId } : {}),
            // A picked article travels as the catalogue reference, also on a stored line whose
            // article was just replaced; a line without one is free text with its own name/code.
            // Its text always travels, so a lab's own wording on a picked article is kept
            // (DD-3583); the code of a picked article stays the catalogue's.
            ...(row._id ? { materialId: row._id } : {}),
            ...(row.description ? { name: row.description } : {}),
            ...(!row._id ? { code: row.materialCode || undefined } : {}),
            batchNumber: row.batchNumber || undefined,
            quantity: toNumber(row.quantity, 1),
            unitPrice: toNumber(row.unitPrice),
            vatPercent: toNumber(row.vatPercent),
            ...(row.isActionMaterial && row.actionId && refByActionId.get(row.actionId)
                ? { relatedActionRef: refByActionId.get(row.actionId) }
                : {}),
        }));
};

export const labConstructionToRestorationInput = (construction: LabConstuction): RestorationInput => ({
    ...(construction._id ? { _id: construction._id } : {}),
    teeth: teethInputFromNumbers(construction.teethNumbers, construction.teethState),
    properties: propertiesToInputs(
        { ...construction.properties, warrantyType: construction.warrantyType },
        { warranty: construction.warranty },
    ),
    warranty: Boolean(construction.warranty),
    isTioTandvard: Boolean(construction.isTioTandvard),
    actions: gridToRestorationActionInputs(construction.actions),
    materials: gridToRestorationMaterialInputs(construction.materials, construction.actions),
});

/** Stored action rows as the action grid shows them. */
/**
 * DD-3620: the server withholds the money on a restoration row from the
 * restricted external tier, keeping only the units. These two mappers feed the
 * lab's own editing grids, which that tier cannot reach, so an absent amount
 * here is an unpriced row — zero is what the grid already shows for one.
 */
type SettledPrice = { [Field in keyof RestorationPrice]-?: NonNullable<RestorationPrice[Field]> };

const priceOf = (price: RestorationPrice): SettledPrice => ({
    quantity: price.quantity,
    unitPrice: price.unitPrice ?? 0,
    vatPercent: price.vatPercent ?? 0,
    totalWithoutVat: price.totalWithoutVat ?? 0,
    totalWithVat: price.totalWithVat ?? 0,
    originalUnitPrice: price.originalUnitPrice ?? 0,
    originalTotalWithVat: price.originalTotalWithVat ?? 0,
});

export const restorationActionsToGridInputs = (rows: RestorationAction[]): ActionGridInput[] =>
    rows.map((row) => ({
        _id: row.actionId ?? row._id,
        rowId: row._id,
        actionId: row.actionId ?? '',
        actionCode: row.code ?? '',
        description: row.name,
        category: row.categoryName ?? row.category ?? '',
        quantity: String(row.price.quantity),
        // Monetary figures are null for a limited external technician (DD-3493),
        // who never reaches the edit grids — the fallback only satisfies the types.
        basePrice: row.price.originalUnitPrice ?? 0,
        unitPrice: String(row.price.unitPrice ?? 0),
        tiotandvardPrice: String(row.price.unitPrice ?? 0),
        vatPercent: String(row.price.vatPercent ?? 0),
        priceWithoutVat: row.price.totalWithoutVat ?? 0,
        priceWithVat: row.price.totalWithVat ?? 0,
        isValid: true,
        originalPrice: row.price.originalUnitPrice ?? 0,
        originalVat: row.price.vatPercent ?? 0,
        quotable: row.quotable ?? undefined,
        costPerTooth: row.costPerTooth ?? undefined,
    }));

/** Stored material rows as the material grid shows them; the surcharge row is not a material. */
export const restorationMaterialsToGridInputs = (
    rows: RestorationMaterial[],
    actionRows: RestorationAction[] = [],
): MaterialGridInput[] =>
    rows
        .filter((row) => !row.isMaterialSurcharge)
        .map((row) => {
            const relatedAction = actionRows.find((action) => action._id === row.relatedActionId);
            const price = priceOf(row.price);
            return {
                _id: row.materialId ?? '',
                rowId: row._id,
                materialCode: row.code ?? '',
                description: row.name,
                batchNumber: row.batchNumber ?? '',
                quantity: String(row.price.quantity),
                // Monetary figures are null for a limited external technician (DD-3493),
                // who never reaches the edit grids — the fallback only satisfies the types.
                basePrice: row.price.originalUnitPrice ?? 0,
                unitPrice: String(row.price.unitPrice ?? 0),
                vatPercent: String(row.price.vatPercent ?? 0),
                priceWithoutVat: row.price.totalWithoutVat ?? 0,
                priceWithVat: row.price.totalWithVat ?? 0,
                isValid: true,
                isActionMaterial: Boolean(relatedAction),
                actionId: relatedAction?.actionId ?? undefined,
                originalPrice: row.price.originalUnitPrice ?? 0,
                originalVat: row.price.vatPercent ?? 0,
            };
        });

/** The stored restorations as the lab flow edits them. */
export const restorationsToLabConstructions = (order: Order): LabConstuction[] =>
    (order.restorations ?? []).map((restoration) => {
        const { teethNumbers, teethState } = teethNumbersFromRestoration(restoration.teeth);
        const bag = propertiesFromRestoration(restoration, { teethAsIds: true });
        const usedProperties = Object.keys(bag).reduce((used, field) => {
            const key = PROPERTY_KEY_BY_FIELD[field];
            return key ? { ...used, [key]: true } : used;
        }, {} as AvailableConstructionProperties);
        return {
            _id: restoration._id,
            teethNumbers,
            teethState,
            actions: restorationActionsToGridInputs(restoration.actions),
            materials: restorationMaterialsToGridInputs(restoration.materials, restoration.actions),
            warranty: isWarrantyRestoration(restoration),
            warrantyType: warrantyTypeOf(restoration) ?? '',
            isTioTandvard: isTioTandvardRestoration(restoration),
            properties: bag as unknown as ConstructionProperty,
            usedProperties,
            isMaterialGridValid: true,
            isActionGridValid: true,
        };
    });

/**
 * A stored restoration as the input that re-submits it unchanged: the rows go back by _id,
 * so the server keeps them as stored (and recounts per-tooth rows when the teeth change).
 * The material surcharge is not sent; the server carries it.
 */
export const restorationAsInput = (restoration: Restoration): RestorationInput => ({
    _id: restoration._id,
    type: restoration.type,
    teeth: teethInputOf(restoration.teeth),
    properties: restorationPropertiesAsInputs(restoration.properties),
    warranty: isWarrantyRestoration(restoration),
    isTioTandvard: isTioTandvardRestoration(restoration),
    actions: restoration.actions.map((row) => ({ _id: row._id })),
    materials: restoration.materials.filter((row) => !row.isMaterialSurcharge).map((row) => ({ _id: row._id })),
});

/**
 * Work made tooth by tooth; the server counts a lab row on such a restoration once per tooth
 * outside action-based orders (backend `restoration-pricing.ts`, PER_TOOTH_TYPES).
 */
const PER_TOOTH_TYPES = new Set<RestorationType>([
    RestorationType.CROWN,
    RestorationType.TEMPORARY_CROWN,
    RestorationType.IMPLANT_CROWN,
    RestorationType.TEMPORARY_IMPLANT_CROWN,
    RestorationType.POST_CORE,
    RestorationType.INLAY,
    RestorationType.TEMPORARY_INLAY,
    RestorationType.ONLAY,
    RestorationType.TEMPORARY_ONLAY,
    RestorationType.VENEER,
    RestorationType.ABUTMENT,
]);

/** The quantity a new lab row on the restoration starts at: one per tooth for per-tooth work. */
export const labRowDefaultQuantity = (restoration: Restoration, actionBased: boolean): number => {
    if (actionBased || !PER_TOOTH_TYPES.has(restoration.type)) return 1;
    const { teeth } = restoration;
    switch (teeth.type) {
        case TeethSelectionType.TEETH:
            return teeth.selectedTeeth?.length || 1;
        case TeethSelectionType.RANGE:
            return teeth.selectedRange
                ? expandRange(teeth.selectedRange.start, teeth.selectedRange.end).length || 1
                : 1;
        default:
            return 1;
    }
};

/**
 * The quantity the server gives a per-tooth action on the restoration (backend
 * `restoration-pricing.ts`, teethCount): its teeth, or the jaws of a jaw selection however the
 * picker sent it — one jaw counts one and both count two, never the teeth in them (DD-3598,
 * DD-3765). A jaw clicked tooth by tooth lists its teeth and is charged per tooth (DD-3585).
 */
export const perToothQuantityOf = (restoration: Restoration): number => {
    const { teeth } = restoration;
    switch (teeth?.type) {
        case TeethSelectionType.TEETH:
            return teeth.selectedTeeth?.length || 1;
        case TeethSelectionType.RANGE: {
            if (!teeth.selectedRange) return 1;
            const jaw = jawOfRange(teeth.selectedRange);
            if (jaw) return jawCountOf(jaw);
            return expandRange(teeth.selectedRange.start, teeth.selectedRange.end).length || 1;
        }
        case TeethSelectionType.JAW:
            if (teeth.selectedTeeth?.length) return teeth.selectedTeeth.length;
            return jawCountOf(teeth.selectedJaw);
        default:
            return 1;
    }
};

/** One restoration's rows re-submitted from the details-page grids. */
export const restorationWithGridRows = (
    restoration: Restoration,
    actionRows: ActionGridInput[],
    materialRows: MaterialGridInput[],
): RestorationInput => ({
    ...restorationAsInput(restoration),
    properties: restorationPropertiesAsInputs(
        restoration.properties,
        actionRows.map((row) => row.rowId),
    ),
    actions: gridToRestorationActionInputs(actionRows),
    materials: gridToRestorationMaterialInputs(materialRows, actionRows),
});

/** The stored teeth re-submitted as they are. */
export const teethInputOf = (teeth: RestorationTeeth): RestorationTeethInput => ({
    type: teeth.type,
    ...(teeth.selectedTeeth?.length ? { selectedTeeth: teeth.selectedTeeth } : {}),
    ...(teeth.selectedRange
        ? { selectedRange: { start: teeth.selectedRange.start, end: teeth.selectedRange.end } }
        : {}),
    ...(teeth.selectedJaw ? { selectedJaw: teeth.selectedJaw } : {}),
    ...(teeth.toothStates?.length
        ? { toothStates: teeth.toothStates.map((entry) => ({ tooth: entry.tooth, state: entry.state })) }
        : {}),
});

/**
 * The stored properties re-submitted as they are. Unit counts name the action row they
 * belong to; given the rows still on the grid, a row no longer there takes its unit count with it.
 */
export const restorationPropertiesAsInputs = (
    properties: RestorationProperty[] | undefined,
    keptActionRowIds?: (string | undefined)[],
): RestorationPropertyInput[] => {
    const inputs: RestorationPropertyInput[] = [];
    (properties ?? []).forEach((property) => {
        switch (property.valueType) {
            case RestorationPropertyValueType.BOOLEAN_VALUE:
                inputs.push({ key: property.key, value: Boolean(property.flag ?? property.value) });
                break;
            case RestorationPropertyValueType.LIST_OF_TEETH:
                inputs.push({ key: property.key, value: property.teeth ?? toothNumbers(property.value) });
                break;
            case RestorationPropertyValueType.VALUE_AND_LABEL: {
                const kept = (property.units ?? [])
                    .filter((unit) => !keptActionRowIds || keptActionRowIds.includes(unit.actionRowId))
                    .map((unit) => ({ actionRef: unit.actionRowId, label: unit.label, value: unit.value }));
                if (kept.length) inputs.push({ key: property.key, value: kept });
                break;
            }
            default:
                if (property.value !== null && property.value !== undefined && property.value !== '') {
                    inputs.push({ key: property.key, value: property.value as string | number });
                }
        }
    });
    return inputs;
};
